import {useRef, useState} from 'react';
import {useRouter} from 'next/router';
import NextLink from 'next/link';
import {useIntl} from 'react-intl';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  Image,
  Text,
  ButtonProps,
  useBoolean,
  Center,
  CenterProps,
  Grid,
  GridProps,
  Divider,
  FlexProps,
  ModalProps,
  Icon,
  useToast,
} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {Slider} from '@eksab/components';
import {useMeQuery} from '@eksab/hooks';
import {
  ArrowDownCircleIcon,
  BackIcon,
  CalendarIcon2 as CalendarIcon,
  CoinsIcon,
  SuccessIcon,
  XIcon2 as XIcon,
} from '@eksab/assets/icons';
import {getLanguage} from '@eksab/util';

import {GoldTrophyIcon} from '../../ranking/icons';
import type {MultiEntryCompetition} from '../../lineup/useSubmitLineupMutation';
import {useCompetitionQuery, useMultiEntryMutation, useSeasonGamesQuery} from '../../hooks';

interface SuccessfulSubmissionModalProps extends Omit<ModalProps, 'children'> {
  competitions: MultiEntryCompetition[];
}

export function SuccessfulSubmissionModal({competitions, ...props}: SuccessfulSubmissionModalProps) {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [totalJoinFees, setTotalJoinFees] = useState(0);
  const [selectedCompetitions, setSelectedCompetitions] = useState<Set<number>>(() => new Set());
  const {locale, query} = useRouter();
  const competitionID = query.slug as string;
  const format = useIntl().formatNumber;
  const dateFormatter = useIntl().formatDate;
  const toast = useToast();
  const [viewCompetitions, {toggle: toggleCompetitions}] = useBoolean();
  const [viewMatches, {toggle: toggleMatches}] = useBoolean();

  const t = useTranslate();
  const {data: me} = useMeQuery();
  const {data: competition} = useCompetitionQuery(competitionID);
  const {data: games} = useSeasonGamesQuery({season: competition?.season.id!}, 'participant');
  const multiEntryMutation = useMultiEntryMutation(competitionID, {
    onSuccess: () => {
      toast({
        title: t('fantasy.lineup.multi-entry.success', {competitions: <em>{[...selectedCompetitions].join(', ')}</em>}),
        status: 'success',
      });

      props.onClose();
    },
    onError: (e) => toast({title: e.response?.data?.message ?? t('app.errors.try-again'), status: 'error'}),
  });

  const available = (me?.coins ?? 0) - totalJoinFees;
  const canJoin = available >= 0;

  const league = competition?.leagues?.[0]?.name;
  const startDate = competition?.season.start!;
  const endDate = competition?.season.end!;
  const endsInSameDay = dateFormatter(startDate) === dateFormatter(endDate);

  const formatDate = (date: string) => dateFormatter(date, {day: 'numeric', month: 'short', year: undefined});

  const onCompetitionClick = ({id, entry_fee}: MultiEntryCompetition) => {
    const _selectedCompetitions = new Set(selectedCompetitions);

    if (_selectedCompetitions.has(id)) {
      _selectedCompetitions.delete(id);
      setTotalJoinFees((prevFees) => prevFees - entry_fee);
    } else {
      _selectedCompetitions.add(id);
      setTotalJoinFees((prevFees) => prevFees + entry_fee);
    }

    setSelectedCompetitions(_selectedCompetitions);
  };

  const onJoinSelected = () => {
    mixpanel?.track('Button Clicked', {
      button: 'lineup.multi-entry.join-selected',
      selected: [...selectedCompetitions],
    });
    multiEntryMutation.mutate({competition_ids: [...selectedCompetitions]});
  };

  return (
    <Modal isCentered closeOnOverlayClick={false} size="sm" {...props}>
      <ModalOverlay />
      <ModalContent
        pos="relative"
        borderWidth="1px"
        borderColor="#dfdfdf"
        rounded="3xl"
        px="6"
        maxH={['90vh', null, '80vh']}
        overflowY="auto"
        overflowX="hidden"
      >
        <ModalHeader textAlign="center" px="0">
          <SuccessIcon />

          <Text as="h2" fontSize="xl" fontWeight="700" my="2">
            {t('fantasy.lineup.submit.success')}
          </Text>

          <Text fontSize="md" color="#949494" mt="2">
            {t('fantasy.lineup.multi-entry.description')}
          </Text>
        </ModalHeader>

        <ModalBody display="flex" flexDir="column" gap="3" p="0" ref={modalBodyRef}>
          <ToggleCompetitionsButton isOpen={viewCompetitions} onClick={toggleCompetitions} />

          {viewCompetitions && (
            <Flex flexDir="column" gap="2" mb="1">
              <Flex
                align="center"
                justify="space-between"
                bg="#101010"
                borderWidth="2px"
                borderColor={canJoin ? '#101010' : '#fb2121'}
                px="4"
                py="2"
                rounded="full"
                color={canJoin ? 'white' : '#de7676'}
                fontSize="sm"
                fontWeight="700"
                pos="sticky"
                top="1"
                zIndex="10"
                onClick={() =>
                  canJoin ? undefined : modalBodyRef.current?.scrollIntoView({block: 'start', behavior: 'smooth'})
                }
              >
                {t('fantasy.lineup.multi-entry.available-coins')}

                <Flex align="center" gap="1" bg={canJoin ? '#2a2e3a' : '#530f0f'} px="3" py="2" rounded="full">
                  {format(available)}

                  <CoinsIcon w="4" />
                </Flex>
              </Flex>

              {!canJoin && (
                <Flex
                  mt="-0.5"
                  mb="2"
                  align="center"
                  justify="space-between"
                  gap="6"
                  px="2"
                  fontSize="xs"
                  fontWeight="600"
                  color="#fb2121"
                >
                  {t('app.errors.no-enough-coins')}

                  <BuyCoinsButton minW="28" />
                </Flex>
              )}

              <Flex justify="space-between" align="center" gap="2">
                <Flex px="3" py="2.5" bg="linear-gradient(270deg, #1f1a1e, #402f3e 103.27%)" rounded="full" maxW="48">
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    bg="linear-gradient(93deg, #fdd1ea, #fee1cc 88%)"
                    backgroundClip="text"
                    sx={{textFillColor: 'transparent'}}
                    noOfLines={1}
                  >
                    {league}
                  </Text>
                </Flex>

                <ToggleMatchesButton isOpen={viewMatches} onClick={toggleMatches} />
              </Flex>

              {viewMatches && (
                <Slider mt="1">
                  {games!?.map(({match}) => <MatchCard key={match.id} match={match} minW="full" />)}
                </Slider>
              )}

              <Flex align="center" justify="space-between" mt="1.5">
                <Flex flexDir="column" gap="1">
                  <Text as="h3" color="#2a2e3a" fontWeight="700">
                    {format(competitions.length)} {t('app.competitions')}
                  </Text>

                  <Text color="#ababab" fontSize="sm" fontWeight="600">
                    {t('app.actions.tap-to-select')}
                  </Text>
                </Flex>

                <Center gap="1.5" bg="#e2fff1" p="3" fontWeight="700" fontSize="sm" rounded="full" color="#2a2e3a">
                  <CalendarIcon width="4" height="auto" color="#200e32" />

                  {endsInSameDay ? formatDate(startDate) : `${formatDate(startDate)} - ${formatDate(endDate)}`}
                </Center>
              </Flex>

              <Flex flexDir="column" gap="2" mt="-0.5">
                {competitions.map((competition) => (
                  <CompetitionSelectItem
                    key={competition.id}
                    competition={competition}
                    isSelected={selectedCompetitions.has(competition.id)}
                    onClick={() => onCompetitionClick(competition)}
                  />
                ))}
              </Flex>
            </Flex>
          )}
        </ModalBody>

        <ModalFooter
          pos="sticky"
          bottom="0"
          px="0"
          bg="white"
          justifyContent={viewCompetitions ? 'space-between' : 'center'}
        >
          <Button variant="link" fontWeight="600" color="#757575" textDecor="underline" onClick={props.onClose}>
            {t('app.actions.skip')}
          </Button>

          {viewCompetitions && (
            <Button
              gap="1.5"
              justifyContent={!totalJoinFees || multiEntryMutation.isLoading ? 'center' : 'space-between'}
              minW="15rem"
              h="3.75rem"
              py="2.5"
              ps="2.5"
              pe="4"
              fontSize="md"
              fontWeight="700"
              textTransform="capitalize"
              _after={{pos: 'absolute', inset: 0, bg: '#929292', rounded: 'inherit', mixBlendMode: 'color'}}
              _hover={{}}
              _active={{}}
              _disabled={{color: '#dfdfdf', cursor: 'not-allowed', _after: {content: '""'}}}
              isLoading={multiEntryMutation.isLoading}
              isDisabled={!canJoin || !totalJoinFees || multiEntryMutation.isLoading}
              onClick={onJoinSelected}
            >
              {!!totalJoinFees && (
                <Flex gap="2" align="center" bg="#4fa86a" rounded="full" h="10" p="2">
                  {format(totalJoinFees)}
                  <CoinsIcon w="4" />
                </Flex>
              )}

              <Flex align="center" gap="1.5">
                {t('fantasy.lineup.multi-entry.cta')}
                <BackIcon transform="auto" scaleX={getLanguage(locale) === 'en' ? -1 : 1} width="2.5" strokeWidth="2" />
              </Flex>
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

interface ToggleButton extends ButtonProps {
  isOpen: boolean;
}

const ToggleCompetitionsButton = ({isOpen, ...props}: ToggleButton) => {
  const t = useTranslate();

  return (
    <Button
      gap="3"
      w="full"
      h="3.5rem"
      textTransform="capitalize"
      fontSize="md"
      fontWeight="700"
      bgColor="unset"
      bg="linear-gradient(93deg, #f3189b, #fB6b02 44%, #F3189B4D, #FB6B024D 88%)"
      bgSize="280%"
      bgPos={isOpen ? 'right' : 'left'}
      color={isOpen ? '#2a2e3a' : 'white'}
      borderColor="currentColor"
      borderWidth={isOpen ? '2px' : 0}
      _hover={{}}
      _active={{}}
      transition="color 0.3s 0.1s, background-position 0.7s"
      {...props}
    >
      {t(`app.actions.${isOpen ? 'hide' : 'show'}`)} {t('app.competitions')}
      <ArrowDownCircleIcon
        transform={`rotate(${isOpen ? '0.5turn' : '1turn'})`}
        transition="transform 0.3s"
        color="currentColor"
      />
    </Button>
  );
};

const ToggleMatchesButton = ({isOpen, ...props}: ToggleButton) => {
  const t = useTranslate();

  return (
    <Button
      variant="ghost"
      gap="1"
      h="unset"
      fontSize="xs"
      fontWeight="600"
      px="2"
      py="3"
      rounded="full"
      borderWidth="1px"
      borderColor={isOpen ? 'currentcolor' : '#dfdfdf'}
      color={isOpen ? 'green.400' : '#2a2e3a'}
      transition="border-color 0.5s, color 0.5s"
      {...props}
    >
      {t(`app.actions.${isOpen ? 'hide' : 'show'}`)} {t('competition.matches')}
      <ArrowDownCircleIcon
        boxSize="3.5"
        transform={`rotate(${isOpen ? '0.5turn' : '1turn'})`}
        transition="transform 0.3s"
        color="currentColor"
      />
    </Button>
  );
};

const BuyCoinsButton = (props: CenterProps) => {
  const {locale} = useRouter();

  const t = useTranslate();

  const move = (percentage: number) => `${getLanguage(locale) === 'en' ? '' : '-'}${percentage}%`;

  return (
    <NextLink href="/store" passHref legacyBehavior>
      <Center
        as="a"
        gap="2"
        pos="relative"
        _before={{
          content: '""',
          pos: 'absolute',
          inset: -0.5,
          bg: 'linear-gradient(180deg, #ffbe5e, #d37f00)',
          rounded: 'inherit',
        }}
        _after={{
          content: '""',
          pos: 'absolute',
          inset: 0,
          bg: 'white',
          rounded: 'inherit',
          transform: 'auto',
          transition: 'transform 0.15s',
          transformOrigin: 'left',
        }}
        rounded="full"
        p="2"
        userSelect="none"
        _hover={{'.back-icon': {translateX: move(100)}}}
        _active={{'> p': {color: 'white'}, '.back-icon': {translateX: move(500), color: 'white'}, _after: {scaleX: 0}}}
        {...props}
      >
        <Text
          fontSize="xs"
          fontWeight="500"
          bg="linear-gradient(180deg, #ffbe5e, #d37f00)"
          backgroundClip="text"
          color="transparent"
          zIndex="2"
          transition="color 0.2s"
        >
          {t('app.actions.buy-coins')}
        </Text>

        <BackIcon
          className="back-icon"
          zIndex="2"
          transform="auto"
          color="#d37f00"
          scaleX={getLanguage(locale) === 'en' ? -1 : 1}
          transition="transform 0.3s, color 0.2s 0.1s"
          width="2.5"
          strokeWidth="2"
        />
      </Center>
    </NextLink>
  );
};

interface CompetitionSelectItemProps extends GridProps {
  competition: {name: string; entry_fee: number};
  isSelected: boolean;
}

const CompetitionSelectItem = ({competition, isSelected, ...props}: CompetitionSelectItemProps) => {
  const format = useIntl().formatNumber;

  const t = useTranslate();

  return (
    <Grid
      templateColumns="1fr auto"
      justifyItems="start"
      gap="2"
      px="4"
      py="3"
      bg={isSelected ? '#e1ffeA' : '#f4f4f4'}
      mx="1px"
      outline="1px solid"
      outlineColor={isSelected ? 'green.400' : 'transparent'}
      borderWidth="1px"
      borderColor={isSelected ? 'green.400' : '#dfdfdf'}
      pos="relative"
      rounded="2xl"
      cursor="pointer"
      transition="border-color 0.2s, outline 0.2s, background 0.2s"
      _hover={{borderColor: 'green.400'}}
      {...props}
    >
      <Text
        as="h4"
        fontSize="sm"
        fontWeight="800"
        px="3.5"
        py="1.5"
        bg="white"
        color="#2a2e3a"
        borderColor="#dfdfdf"
        borderWidth="1px"
        rounded="full"
      >
        <GoldTrophyIcon height="6" width="auto" me="1.5" />
        {competition.name}
      </Text>

      <Flex
        gap="2"
        fontSize="xs"
        fontWeight="700"
        px="3"
        py="1.5"
        bg="white"
        color="#2a2e3a"
        rounded="full"
        gridRow="2"
      >
        {t('app.entry_fee')}

        <Divider orientation="vertical" />

        <Flex align="center" gap="1.5" color="#d37f00">
          <CoinsIcon w="4" />
          {format(competition.entry_fee)}
        </Flex>
      </Flex>

      <Checkbox checked={isSelected} alignSelf="center" />
    </Grid>
  );
};

const Checkbox = ({checked, ...props}: FlexProps & {checked: boolean}) => (
  <Flex
    boxSize="1.125rem"
    bg={checked ? 'green.400' : 'white'}
    borderWidth="1px"
    borderColor={checked ? 'green.400' : '#2a2e3a'}
    rounded="0.25rem"
    transition="background 0.2s, border-color 0.2s"
    {...props}
  />
);

interface MatchCardProps extends FlexProps {
  match: MatchT;
}

const MatchCard = ({match, ...props}: MatchCardProps) => {
  const format = useIntl().formatDate;

  const formatDate = (date: string) => format(date, {day: 'numeric', month: 'short', year: 'numeric'});
  const formatTime = (date: string) => format(date, {hour: 'numeric', minute: 'numeric'});

  return (
    <Flex flexDir="column" {...props}>
      <Center gap="2" pos="relative" height="18px" fontSize="xs" fontWeight="600" color="white" py="1">
        <Icon width="189px" height="18px" viewBox="0 0 189 18" fill="none" pos="absolute" top="0" zIndex="-1">
          <path
            d="M189 18L0 18L11.0399 3.84896C12.9349 1.41998 15.8436 0 18.9243 0L170.39 0C173.52 0 176.469 1.46551 178.36 3.96007L189 18Z"
            fill="#141414"
          />
        </Icon>

        {formatDate(match.start)}

        <Divider orientation="vertical" borderColor="white" opacity="1" />

        {formatTime(match.start)}
      </Center>

      <Flex
        pos="relative"
        justify="space-between"
        align="center"
        gap="2"
        bg="white"
        my="1px"
        p="2"
        rounded="2xl"
        _before={{
          content: '""',
          bg: 'linear-gradient(180deg, #ec1958, #ffce84)',
          pos: 'absolute',
          inset: '-1px',
          zIndex: -1,
          rounded: 'inherit',
        }}
      >
        <Team team={match.home_team} />

        <XIcon />

        <Team team={match.away_team} />
      </Flex>
    </Flex>
  );
};

const Team = ({team}: {team: Team}) => (
  <Flex flex={1} flexDir="column" gap="0.5" justify="center">
    <Image src={team.logo?.thumb ?? team.logo?.original} alt={team.name} h="5" w="auto" objectFit="contain" />

    <Text dir="auto" fontSize="xs" fontWeight="600" color="#2a2e3a" textAlign="center" noOfLines={1}>
      {team.name}
    </Text>
  </Flex>
);
